import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link2, Youtube, FileText, Paperclip, Image as ImageIcon, Music, Send, Tag, Folder, Plus, Check, X } from 'lucide-react';
import { isYoutube, extractUrls, normalizeUrl, domainOf } from '../lib/utils';
import { createCategory, createProject } from '../lib/api';
import { useT } from '../lib/LanguageContext';

const IMAGE_MIME_RE = /^image\//;
const AUDIO_MIME_RE = /^audio\//;

const TRANSITION_MS = 220;
const CAT_TRANSITION_MS = 180;
const CAT_LIST_WIDTH = 180;
const LIST_INDENT = '  ';
const BULLET_LIST_RE = /^(\s*)([-*•])(\s+)(.*)$/;
const NUMBER_LIST_RE = /^(\s*)(\d+)([.)])(\s+)(.*)$/;

// ---- Inline link pills in the body editor -----------------------------------
// The body editor is a contentEditable div, not a <textarea> — that's what
// lets a pasted link render as an actual pill INSIDE the text flow ("text
// [pill] text") instead of being pulled out into a separate row above.
//
// The body is still kept as ONE plain string (in React state, `body`) so all
// the existing line-based logic (bullet continuation, undo/redo snapshots)
// keeps working almost unchanged — a link is just encoded inline as a marker
// run using two Private-Use-Area characters that will never appear in real
// user text: <url>. The DOM is the visual source of truth while
// typing (never re-rendered from React state on every keystroke, or the
// caret would jump); the marker string is re-derived from the DOM on input
// and is what actually gets embedded/sent on submit.
const LINK_OPEN = '';
const LINK_CLOSE = '';
const LINK_TOKEN_RE = /([^]*)/g;

function bodyToSegments(markerString) {
  const segments = [];
  let lastIndex = 0;
  LINK_TOKEN_RE.lastIndex = 0;
  let m;
  while ((m = LINK_TOKEN_RE.exec(markerString))) {
    if (m.index > lastIndex) segments.push({ type: 'text', value: markerString.slice(lastIndex, m.index) });
    segments.push({ type: 'link', value: m[1] });
    lastIndex = LINK_TOKEN_RE.lastIndex;
  }
  if (lastIndex < markerString.length) segments.push({ type: 'text', value: markerString.slice(lastIndex) });
  return segments;
}

// Plain text (links stripped out) + the list of link URLs, in reading order —
// what actually gets sent to the backend.
function serializeBody(markerString) {
  const segments = bodyToSegments(markerString);
  let text = '';
  const links = [];
  for (const seg of segments) {
    if (seg.type === 'link') links.push(seg.value);
    else text += seg.value;
  }
  return { text, links };
}

function isLinkPillNode(node) {
  return node?.nodeType === Node.ELEMENT_NODE && node.dataset && node.dataset.linkUrl != null;
}

function nodeMarkerLength(node) {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent.length;
  if (isLinkPillNode(node)) return node.dataset.linkUrl.length + 2;
  if (node.nodeName === 'BR') return 1;
  return 0;
}

// DOM -> marker string. The editor is kept flat (no nested <div>s — Enter is
// always intercepted and a literal "\n" text node is inserted instead, which
// `white-space: pre-wrap` renders as a real line break), so this only ever
// needs to walk direct children.
function domToBody(el) {
  let out = '';
  el.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) out += node.textContent;
    else if (isLinkPillNode(node)) out += `${LINK_OPEN}${node.dataset.linkUrl}${LINK_CLOSE}`;
    else if (node.nodeName === 'BR') out += '\n';
  });
  return out;
}

function makeLinkPill(url) {
  const span = document.createElement('span');
  span.contentEditable = 'false';
  span.dataset.linkUrl = url;
  // Deliberately square — no rounded-* class.
  span.className =
    'inline-flex items-center gap-1 align-baseline mx-0.5 px-1.5 py-0.5 border border-keep-border bg-keep-hover text-[13px] text-keep-urlBlue select-none whitespace-nowrap';
  const icon = document.createElement('span');
  icon.textContent = isYoutube(url) ? '▶' : '🔗';
  icon.className = 'text-[11px] flex-shrink-0';
  const label = document.createElement('span');
  label.textContent = domainOf(url) || url;
  label.className = 'truncate max-w-[240px]';
  span.appendChild(icon);
  span.appendChild(label);
  return span;
}

// Full clear-and-rebuild — only called on mount/reset/undo-redo, never on
// every keystroke (that would fight the browser's native caret handling).
function renderBodyIntoDom(el, markerString) {
  el.replaceChildren();
  for (const seg of bodyToSegments(markerString)) {
    if (seg.type === 'text') {
      if (seg.value) el.appendChild(document.createTextNode(seg.value));
    } else {
      el.appendChild(makeLinkPill(seg.value));
    }
  }
}

function appendTextAtEnd(el, text) {
  if (el.childNodes.length > 0) el.appendChild(document.createTextNode('\n'));
  el.appendChild(document.createTextNode(text));
}

// Caret position expressed as an offset into the MARKER string (a link pill
// counts as its full marker length — the caret can only sit before or after
// one, never "inside" it, since it's contentEditable=false).
function getCaretOffset(el) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!el.contains(range.startContainer)) return null;

  // Caret sits directly in `el` itself, between two sibling nodes (e.g.
  // right after an atomic link pill was inserted, or after arrow-key
  // navigation past one) — `startOffset` there is a CHILD INDEX, not a
  // character offset, and `el` never shows up while walking its own
  // children below, so this has to be handled before the walk.
  if (range.startContainer === el) {
    let offset = 0;
    for (let i = 0; i < range.startOffset; i++) offset += nodeMarkerLength(el.childNodes[i]);
    return offset;
  }

  let offset = 0;
  let found = false;
  const walk = (node) => {
    if (found) return;
    if (node === range.startContainer) {
      if (node.nodeType === Node.TEXT_NODE) {
        offset += range.startOffset;
      } else {
        for (let i = 0; i < range.startOffset; i++) offset += nodeMarkerLength(node.childNodes[i]);
      }
      found = true;
      return;
    }
    offset += nodeMarkerLength(node);
  };
  el.childNodes.forEach(walk);
  return found ? offset : null;
}

function setCaretOffset(el, targetOffset) {
  const sel = window.getSelection();
  const range = document.createRange();
  let remaining = targetOffset;
  let placed = false;
  el.childNodes.forEach((node) => {
    if (placed) return;
    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.textContent.length;
      if (remaining <= len) {
        range.setStart(node, Math.max(0, remaining));
        placed = true;
        return;
      }
      remaining -= len;
    } else {
      const len = nodeMarkerLength(node);
      if (remaining < len) {
        range.setStartBefore(node);
        placed = true;
        return;
      }
      remaining -= len;
    }
  });
  if (!placed) {
    range.selectNodeContents(el);
    range.collapse(false);
  } else {
    range.collapse(true);
  }
  sel.removeAllRanges();
  sel.addRange(range);
}

function insertLinkPillAtCursor(el, url) {
  // Read the CURRENT selection before touching focus — el.focus() on a
  // contentEditable with no prior selection inside it makes the browser
  // synthesize one at the very start of its content, which would otherwise
  // get picked up below and silently redirect the insert to offset 0.
  const sel = window.getSelection();
  let range;
  if (sel && sel.rangeCount && el.contains(sel.anchorNode)) {
    range = sel.getRangeAt(0);
  } else {
    range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
  }
  range.deleteContents();
  const pill = makeLinkPill(url);
  range.insertNode(pill);
  range.setStartAfter(pill);
  range.collapse(true);
  el.focus();
  sel.removeAllRanges();
  sel.addRange(range);
}

const TakeNote = forwardRef(function TakeNote({
  onCreateNote,
  busy,
  categories,
  onCategoriesChanged,
  projects,
  onProjectsChanged,
  forceExpanded = false,
  onClose,
  onHistoryChange,
}, ref) {
  const t = useT();
  const [expanded, setExpanded] = useState(forceExpanded);
  const isExpanded = forceExpanded || expanded;
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [categoryId, setCategoryId] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [projPickerOpen, setProjPickerOpen] = useState(false);
  const projPickerRef = useRef(null);
  // Mounted vs. open are separate so the dropdown can play a closing
  // transition before it actually leaves the DOM, instead of vanishing
  // instantly. The transition itself is a height grow/shrink (an accordion,
  // not a scale) — catHeightPx is the pinned px value while animating, or
  // null for 'auto' once fully open so a changing list isn't clipped.
  const [catPickerMounted, setCatPickerMounted] = useState(false);
  const [catPickerOpen, setCatPickerOpen] = useState(false);
  const [catHeightPx, setCatHeightPx] = useState(0);
  const catPickerBoxRef = useRef(null);
  const catPickerCloseTimerRef = useRef(null);
  const catPickerOpenTimerRef = useRef(null);
  // The merged shape is a "flag" — full-width where the list is, narrow
  // where just the category button is — cut with clip-path so the button
  // row's own footprint (measured here) doesn't cover whatever sits next
  // to it (the attach button).
  const catButtonRowRef = useRef(null);
  const [catBtnDims, setCatBtnDims] = useState({ w: 0, h: 0 });
  const [pdfFiles, setPdfFiles] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [audioFiles, setAudioFiles] = useState([]);
  const fileRef = useRef(null);
  const imageFileRef = useRef(null);
  const audioFileRef = useRef(null);
  const wrapperRef = useRef(null);
  const formRef = useRef(null);
  const collapsedMeasureRef = useRef(null);
  const openingRef = useRef(false);
  const bodyRef = useRef(null);
  // Explicit pixel height pinned only while the card is growing/shrinking —
  // 'auto' the rest of the time so the textarea can still grow normally
  // while typing. Driving an actual height transition (rather than an
  // opacity/scale fade) is what makes open/close read as "expand downward /
  // collapse back to default" instead of a generic fade.
  const [heightPx, setHeightPx] = useState(null);

  // Undo/redo history for the title + body text — only wired up for the
  // full-screen mobile composer, which exposes its own undo/redo buttons
  // in the header (native browser undo doesn't reliably track controlled
  // inputs, so we keep our own snapshot stack instead).
  const historyRef = useRef([{ title: '', body: '' }]);
  const historyIndexRef = useRef(0);
  const skipHistoryRef = useRef(false);
  const historyMountedRef = useRef(false);

  useEffect(() => {
    if (!forceExpanded) return;
    if (!historyMountedRef.current) {
      // The ref above already seeds the initial snapshot — this first
      // effect run is just mount, not a real edit, so skip the push.
      historyMountedRef.current = true;
      return;
    }
    if (skipHistoryRef.current) {
      skipHistoryRef.current = false;
      return;
    }
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push({ title, body });
    historyIndexRef.current = historyRef.current.length - 1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, body, forceExpanded]);

  const restoreSnapshot = (snap) => {
    skipHistoryRef.current = true;
    setTitle(snap.title);
    setBody(snap.body);
    if (bodyRef.current) {
      renderBodyIntoDom(bodyRef.current, snap.body);
      bodyRef.current.focus();
      setCaretOffset(bodyRef.current, snap.body.length);
    }
  };

  const undo = () => {
    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current -= 1;
    restoreSnapshot(historyRef.current[historyIndexRef.current]);
  };

  const redo = () => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    historyIndexRef.current += 1;
    restoreSnapshot(historyRef.current[historyIndexRef.current]);
  };

  const canUndo = historyIndexRef.current > 0;
  const canRedo = historyIndexRef.current < historyRef.current.length - 1;

  useEffect(() => {
    if (!forceExpanded) return;
    onHistoryChange?.({ canUndo, canRedo, undo, redo });
    // undo/redo close over refs only, so they're stable in every meaningful
    // sense even though the lint rule can't see that — re-running this
    // effect only needs to track the two booleans that actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUndo, canRedo, forceExpanded]);

  // The card is closing entirely, so the dropdown just needs to be gone —
  // no point animating it out underneath the card's own close animation.
  const closeCatPickerImmediately = () => {
    if (catPickerCloseTimerRef.current) {
      clearTimeout(catPickerCloseTimerRef.current);
      catPickerCloseTimerRef.current = null;
    }
    if (catPickerOpenTimerRef.current) {
      clearTimeout(catPickerOpenTimerRef.current);
      catPickerOpenTimerRef.current = null;
    }
    setCatPickerOpen(false);
    setCatPickerMounted(false);
    setCatHeightPx(0);
  };

  const openCatPicker = () => {
    if (catPickerCloseTimerRef.current) {
      clearTimeout(catPickerCloseTimerRef.current);
      catPickerCloseTimerRef.current = null;
    }
    if (catPickerOpenTimerRef.current) {
      clearTimeout(catPickerOpenTimerRef.current);
      catPickerOpenTimerRef.current = null;
    }
    setCatHeightPx(0);
    setCatPickerMounted(true);
    setCatPickerOpen(true);
    // Double rAF: mounting at height 0 has to actually paint before we grow
    // it, otherwise React can commit both the 0 and the target height in
    // the same frame and the browser never renders the starting point —
    // the menu just pops open instead of growing (same root cause as the
    // mobile FAB morph needing a double rAF).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const target = catPickerBoxRef.current?.scrollHeight;
        if (target) setCatHeightPx(target);
        catPickerOpenTimerRef.current = setTimeout(() => {
          setCatHeightPx(null); // release to auto once fully open
        }, CAT_TRANSITION_MS + 20);
      });
    });
  };

  const closeCatPicker = () => {
    if (catPickerOpenTimerRef.current) {
      clearTimeout(catPickerOpenTimerRef.current);
      catPickerOpenTimerRef.current = null;
    }
    const current = catPickerBoxRef.current?.getBoundingClientRect().height;
    if (current) setCatHeightPx(current);
    setCatPickerOpen(false);
    requestAnimationFrame(() => setCatHeightPx(0));
    catPickerCloseTimerRef.current = setTimeout(() => setCatPickerMounted(false), CAT_TRANSITION_MS);
  };

  const toggleCatPicker = () => {
    if (catPickerMounted && catPickerOpen) closeCatPicker();
    else openCatPicker();
  };

  const reset = () => {
    setExpanded(false);
    setTitle('');
    setBody('');
    if (bodyRef.current) bodyRef.current.replaceChildren();
    setCategoryId(null);
    setProjectId(null);
    setProjPickerOpen(false);
    closeCatPickerImmediately();
    setPdfFiles([]);
    setImageFiles([]);
    setAudioFiles([]);
    setHeightPx(null);
    historyRef.current = [{ title: '', body: '' }];
    historyIndexRef.current = 0;
    if (forceExpanded) onClose?.();
  };

  // Opening the desktop card: pin the wrapper at its current (collapsed)
  // height, swap in the expanded form, then grow to the form's real height
  // on the next frame so the height change actually transitions instead of
  // snapping. The effect below does the growing; this just arms it.
  const startExpand = () => {
    if (forceExpanded || isExpanded || openingRef.current) return;
    openingRef.current = true;
    const collapsedH = collapsedMeasureRef.current?.getBoundingClientRect().height;
    if (collapsedH) setHeightPx(collapsedH);
    setExpanded(true);
  };

  useEffect(() => {
    if (forceExpanded || !openingRef.current) return;
    const raf = requestAnimationFrame(() => {
      if (!openingRef.current) return;
      const target = formRef.current?.scrollHeight;
      if (target) setHeightPx(target);
    });
    // Once the grow transition has finished, release back to 'auto' so a
    // growing textarea isn't fighting a stale pixel cap while typing.
    const release = setTimeout(() => {
      if (openingRef.current) {
        setHeightPx(null);
        openingRef.current = false;
      }
    }, TRANSITION_MS + 30);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(release);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, forceExpanded]);

  // Closing mirrors opening exactly: pin the current (auto) height as a
  // concrete px value, fade the content out, then shrink to the collapsed
  // row's height on the next frame so it's a real reverse-of-expand
  // transition rather than a fade/shrink.
  const [closing, setClosing] = useState(false);
  const closeCard = () => {
    if (forceExpanded || !isExpanded) {
      reset();
      return;
    }
    openingRef.current = false;
    const current = wrapperRef.current?.getBoundingClientRect().height;
    if (current) setHeightPx(current);
    setClosing(true);
    requestAnimationFrame(() => {
      const target = collapsedMeasureRef.current?.getBoundingClientRect().height;
      if (target) setHeightPx(target);
    });
    setTimeout(() => {
      setClosing(false);
      reset();
    }, TRANSITION_MS);
  };

  // Everything (text + inline links + images + pdfs) goes out as ONE
  // combined note — the backend embeds each attachment as its own chunk
  // under the same doc, so bundling them here doesn't blur their content
  // together.
  const submit = async () => {
    const { text: rawText, links: pillLinks } = serializeBody(body);
    let text = rawText.trim();
    const links = [...pillLinks];
    // A lone URL still sitting as plain text (typed, not pasted-as-a-pill)
    // is pulled out the same way it always was — rest of the text becomes
    // the note body instead of being embedded as a raw URL.
    const urls = extractUrls(text);
    if (urls.length === 1) {
      links.push(normalizeUrl(urls[0]));
      text = text.replace(urls[0], '').trim();
    }
    const hasContent =
      !!text || links.length > 0 || pdfFiles.length > 0 || imageFiles.length > 0 || audioFiles.length > 0;
    if (!hasContent) {
      closeCard();
      return;
    }
    const imagesOnlyNeedCaption =
      imageFiles.length > 0 &&
      !text &&
      links.length === 0 &&
      pdfFiles.length === 0 &&
      audioFiles.length === 0;
    if (imagesOnlyNeedCaption) return; // caption required, submit button is disabled anyway

    onCreateNote({
      body: text,
      title: title.trim() || null,
      links,
      categoryId,
      projectId,
      images: imageFiles,
      pdfs: pdfFiles,
      audios: audioFiles,
    });
    closeCard();
  };

  // Click-outside-to-close, driven by 'pointerdown' rather than blur/focus
  // (blur fires with relatedTarget null the moment the native OS file
  // picker opens, which used to collapse the form out from under a pending
  // attachment pick) — and NOT 'mousedown': the notes grid's drag-select
  // handler calls preventDefault() on its own pointerdown for mouse input,
  // which suppresses the browser's synthetic mousedown/click entirely, so
  // a mousedown listener here never sees clicks landing on the grid.
  useEffect(() => {
    if (forceExpanded || !isExpanded) return;
    const onDocPointerDown = (e) => {
      if (!wrapperRef.current || wrapperRef.current.contains(e.target)) return;
      if (
        !body.trim() &&
        !title.trim() &&
        pdfFiles.length === 0 &&
        imageFiles.length === 0 &&
        audioFiles.length === 0
      ) {
        closeCard();
      }
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceExpanded, isExpanded, body, title, pdfFiles, imageFiles, audioFiles]);

  const openFilePicker = () => fileRef.current?.click();
  const openImagePicker = () => imageFileRef.current?.click();
  const openAudioPicker = () => audioFileRef.current?.click();

  const handleFile = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      setPdfFiles((prev) => [...prev, ...files]);
      startExpand();
    }
    e.target.value = '';
  };

  const handleImageFile = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      setImageFiles((prev) => [...prev, ...files]);
      startExpand();
    }
    e.target.value = '';
  };

  const handleAudioFile = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      setAudioFiles((prev) => [...prev, ...files]);
      startExpand();
    }
    e.target.value = '';
  };

  const removePdfFile = (index) => {
    setPdfFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeImageFile = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeAudioFile = (index) => {
    setAudioFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Object URLs are only recomputed when the file list itself changes (not on
  // every keystroke) and revoked on cleanup, so they don't leak.
  const imagePreviewUrls = useMemo(
    () => imageFiles.map((f) => URL.createObjectURL(f)),
    [imageFiles],
  );
  useEffect(() => {
    return () => imagePreviewUrls.forEach((u) => URL.revokeObjectURL(u));
  }, [imagePreviewUrls]);

  // The body div is uncontrolled (DOM is the live source of truth while
  // typing) — this just mirrors it into React state so submit/hints/undo
  // stay in sync. Never rebuilds the DOM itself.
  const handleBodyInput = () => {
    if (!bodyRef.current) return;
    setBody(domToBody(bodyRef.current));
  };

  // Paste a screenshot/image straight from the clipboard (Cmd/Ctrl+V) —
  // designers grabbing a color/layout reference shouldn't need a file picker.
  // stopPropagation so this doesn't ALSO get picked up by App.js's
  // document-level paste listener (that one only handles paste when focus
  // is outside this component entirely, to auto-open the collapsed card).
  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItems = items.filter((it) => IMAGE_MIME_RE.test(it.type));
    if (imageItems.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      const files = imageItems.map((it) => it.getAsFile()).filter(Boolean);
      if (files.length) {
        setImageFiles((prev) => [...prev, ...files]);
        startExpand();
      }
      return;
    }

    const pasted = (e.clipboardData?.getData('text/plain') || '').trim();
    if (!pasted) return;

    // A paste that's ENTIRELY a link (nothing else in the clipboard text) —
    // insert it as a pill right where the cursor is, regardless of which
    // field actually has focus (matches the old behavior of pulling a
    // pasted link into its own chip even if Title happened to be focused).
    const urls = extractUrls(pasted);
    if (urls.length === 1 && pasted.replace(urls[0], '').trim() === '') {
      e.preventDefault();
      e.stopPropagation();
      startExpand();
      requestAnimationFrame(() => {
        const el = bodyRef.current;
        if (!el) return;
        insertLinkPillAtCursor(el, normalizeUrl(urls[0]));
        setBody(domToBody(el));
      });
      return;
    }

    // Any other paste is left alone (Title still pastes plain text
    // natively) — EXCEPT landing directly in the contentEditable body, where
    // the browser's default paste would bring in rich HTML formatting from
    // the clipboard; that's swapped for a plain-text insert so the body
    // never picks up formatting a plain <textarea> never would have shown.
    if (e.target === bodyRef.current) {
      e.preventDefault();
      bodyRef.current.focus();
      document.execCommand('insertText', false, pasted);
      setBody(domToBody(bodyRef.current));
    }
  };

  // Drag-and-drop multi-select from the file explorer — images and PDFs are
  // routed by mime type, anything else is ignored. stopPropagation for the
  // same reason as handlePaste above.
  const [dragActive, setDragActive] = useState(false);
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length) return;
    const images = files.filter((f) => IMAGE_MIME_RE.test(f.type));
    const pdfs = files.filter((f) => f.type === 'application/pdf');
    const audios = files.filter((f) => AUDIO_MIME_RE.test(f.type));
    if (images.length) setImageFiles((prev) => [...prev, ...images]);
    if (pdfs.length) setPdfFiles((prev) => [...prev, ...pdfs]);
    if (audios.length) setAudioFiles((prev) => [...prev, ...audios]);
    if (images.length || pdfs.length || audios.length) startExpand();
  };

  // Imperative API for App.js's document-level paste/drop listeners, which
  // handle the case where the user pastes/drops WITHOUT having clicked "Take
  // a note" first — auto-opens the card, stages the content, and focuses the
  // body so the user can keep typing immediately.
  useImperativeHandle(ref, () => ({
    stageExternalImages(files) {
      setImageFiles((prev) => [...prev, ...files]);
      startExpand();
      requestAnimationFrame(() => bodyRef.current?.focus());
    },
    stageExternalPdfs(files) {
      setPdfFiles((prev) => [...prev, ...files]);
      startExpand();
      requestAnimationFrame(() => bodyRef.current?.focus());
    },
    stageExternalText(text) {
      startExpand();
      requestAnimationFrame(() => {
        const el = bodyRef.current;
        if (!el) return;
        appendTextAtEnd(el, text);
        setBody(domToBody(el));
        el.focus();
        setCaretOffset(el, domToBody(el).length);
      });
    },
    stageExternalLink(url) {
      startExpand();
      requestAnimationFrame(() => {
        const el = bodyRef.current;
        if (!el) return;
        insertLinkPillAtCursor(el, url);
        setBody(domToBody(el));
      });
    },
  }));

  // Auto-continues "- " bullets and "1. " numbered lists on Enter (and
  // ends the list when Enter is pressed on an already-empty item, like
  // Keep/Notion/etc.), plus Tab/Shift+Tab to indent/outdent into a nested
  // list — renumbering numbered items against their siblings at that
  // indent level so nesting doesn't just keep counting up in one sequence.
  //
  // Enter is ALSO intercepted for the non-list case (plain line break) —
  // contentEditable's native Enter behavior inserts a nested <div>/<br> that
  // varies by browser, so every line break here is a literal "\n" text
  // character instead (rendered as a real line break via `white-space:
  // pre-wrap`), keeping the editor's DOM flat and easy to read back out.
  const applyBodyEdit = (newValue, newCaretPos) => {
    const el = bodyRef.current;
    if (!el) return;
    renderBodyIntoDom(el, newValue);
    el.focus();
    setCaretOffset(el, newCaretPos);
    setBody(newValue);
  };

  const handleBodyKeyDown = (e) => {
    const el = bodyRef.current;
    if (!el) return;
    const sel = window.getSelection();
    if (!sel || !sel.isCollapsed) return; // only handle a plain caret, not a selection

    const value = domToBody(el);
    const selectionStart = getCaretOffset(el);
    if (selectionStart == null) return;

    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    let lineEnd = value.indexOf('\n', selectionStart);
    if (lineEnd === -1) lineEnd = value.length;
    const line = value.slice(lineStart, lineEnd);

    if (e.key === ' ') {
      // Typing "- " or "* " at the start of a line converts it to a "• "
      // dot bullet as you type, the way Docs/Notion do — so the marker you
      // see (and that Enter/Tab continue) is always the dot, regardless of
      // which key started it.
      const beforeCursor = value.slice(lineStart, selectionStart);
      const dashMatch = beforeCursor.match(/^(\s*)[-*]$/);
      if (dashMatch) {
        e.preventDefault();
        const indent = dashMatch[1];
        const newValue = value.slice(0, lineStart) + indent + '• ' + value.slice(selectionStart);
        applyBodyEdit(newValue, lineStart + indent.length + 2);
      }
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const bulletMatch = line.match(BULLET_LIST_RE);
      const numMatch = line.match(NUMBER_LIST_RE);
      if (!bulletMatch && !numMatch) {
        const newValue = value.slice(0, selectionStart) + '\n' + value.slice(selectionStart);
        applyBodyEdit(newValue, selectionStart + 1);
        return;
      }

      if ((bulletMatch && bulletMatch[4].trim() === '') || (numMatch && numMatch[5].trim() === '')) {
        // Enter on an empty list item exits the list instead of continuing it.
        const indent = (bulletMatch || numMatch)[1];
        const newValue = value.slice(0, lineStart) + indent + value.slice(lineEnd);
        applyBodyEdit(newValue, lineStart + indent.length);
        return;
      }

      const prefix = bulletMatch
        ? `${bulletMatch[1]}${bulletMatch[2]} `
        : `${numMatch[1]}${parseInt(numMatch[2], 10) + 1}${numMatch[3]} `;
      const newValue = value.slice(0, selectionStart) + '\n' + prefix + value.slice(selectionStart);
      applyBodyEdit(newValue, selectionStart + 1 + prefix.length);
      return;
    }

    if (e.key === 'Tab') {
      const bulletMatch = line.match(BULLET_LIST_RE);
      const numMatch = line.match(NUMBER_LIST_RE);
      if (!bulletMatch && !numMatch) return;
      e.preventDefault();

      const [, indent] = bulletMatch || numMatch;
      const newIndent = e.shiftKey
        ? indent.startsWith(LIST_INDENT)
          ? indent.slice(LIST_INDENT.length)
          : ''
        : indent + LIST_INDENT;

      let newLine;
      if (bulletMatch) {
        newLine = `${newIndent}${bulletMatch[2]} ${bulletMatch[4]}`;
      } else {
        let newNum = 1;
        const beforeLines = value.slice(0, lineStart).split('\n');
        for (let i = beforeLines.length - 1; i >= 0; i--) {
          const l = beforeLines[i];
          if (l.trim() === '') continue;
          const leading = l.match(/^\s*/)[0];
          if (leading.length < newIndent.length) break;
          if (leading.length === newIndent.length) {
            const m = l.match(NUMBER_LIST_RE);
            if (m) newNum = parseInt(m[2], 10) + 1;
            break;
          }
        }
        newLine = `${newIndent}${newNum}${numMatch[3]} ${numMatch[5]}`;
      }

      const newValue = value.slice(0, lineStart) + newLine + value.slice(lineEnd);
      const caretOffsetFromEnd = lineEnd - selectionStart;
      const newPos = Math.max(lineStart, lineStart + newLine.length - caretOffsetFromEnd);
      applyBodyEdit(newValue, newPos);
    }
  };

  const addNewCat = async () => {
    const n = window.prompt(t('takeNote.newCategoryPrompt'));
    if (!n || !n.trim()) return;
    try {
      const c = await createCategory(n.trim());
      setCategoryId(c.id);
      closeCatPicker();
      onCategoriesChanged?.();
    } catch {}
  };

  const addNewProj = async () => {
    const n = window.prompt(t('takeNote.newProjectPrompt'));
    if (!n || !n.trim()) return;
    try {
      const p = await createProject(n.trim());
      setProjectId(p.id);
      setProjPickerOpen(false);
      onProjectsChanged?.();
    } catch {}
  };

  useEffect(() => {
    if (!projPickerOpen) return;
    const onDocPointerDown = (e) => {
      if (!projPickerRef.current || projPickerRef.current.contains(e.target)) return;
      setProjPickerOpen(false);
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
  }, [projPickerOpen]);

  const selectedCat = (categories || []).find((c) => c.id === categoryId);
  const selectedProj = (projects || []).find((p) => p.id === projectId);

  useLayoutEffect(() => {
    if (!catPickerMounted || !catButtonRowRef.current) return;
    const rect = catButtonRowRef.current.getBoundingClientRect();
    setCatBtnDims({ w: rect.width, h: rect.height });
  }, [catPickerMounted, selectedCat]);

  // Flag-shaped clip: full CAT_LIST_WIDTH where the list is, narrow to just
  // the button row's own measured width at the row itself. calc(100% - Npx)
  // for the row's fixed edge keeps the notch pinned to the button row even
  // while the list portion is mid-height-animation (100% tracks whatever
  // the box's current animated height is, each frame, automatically).
  const catClipPath =
    catPickerMounted && catBtnDims.w > 0
      ? forceExpanded
        ? `polygon(0 0, ${CAT_LIST_WIDTH}px 0, ${CAT_LIST_WIDTH}px calc(100% - ${catBtnDims.h}px), ${catBtnDims.w}px calc(100% - ${catBtnDims.h}px), ${catBtnDims.w}px 100%, 0 100%)`
        : `polygon(0 0, ${catBtnDims.w}px 0, ${catBtnDims.w}px ${catBtnDims.h}px, ${CAT_LIST_WIDTH}px ${catBtnDims.h}px, ${CAT_LIST_WIDTH}px 100%, 0 100%)`
      : undefined;

  const { text: plainBodyText, links: pillLinks } = useMemo(() => serializeBody(body), [body]);
  const trimmedBody = plainBodyText.trim();
  const detectedUrls = extractUrls(trimmedBody);
  const urlDetected = detectedUrls.length === 1;
  const multiUrlDetected = detectedUrls.length > 1;
  const ytDetected = urlDetected && isYoutube(detectedUrls[0]);
  // Images require a non-empty caption/link/pdf alongside them (the only
  // searchable text an image attachment gets, since there's no OCR) — every
  // other combination just needs *something* present to submit.
  const hasAnyContent =
    !!trimmedBody ||
    pillLinks.length > 0 ||
    urlDetected ||
    pdfFiles.length > 0 ||
    imageFiles.length > 0 ||
    audioFiles.length > 0;
  const imagesOnlyNeedCaption =
    imageFiles.length > 0 &&
    !trimmedBody &&
    pillLinks.length === 0 &&
    !urlDetected &&
    pdfFiles.length === 0 &&
    audioFiles.length === 0;
  const submitDisabled = busy || !hasAnyContent || imagesOnlyNeedCaption;

  const bodyPlaceholder =
    imageFiles.length > 0
      ? t('takeNote.bodyPlaceholderImage')
      : pillLinks.length > 0
        ? t('takeNote.bodyPlaceholderLink')
        : pdfFiles.length > 0 || audioFiles.length > 0
          ? t('takeNote.bodyPlaceholderFile')
          : t('takeNote.bodyPlaceholderDefault');

  return (
    <div
      className={
        forceExpanded
          ? 'flex-1 min-h-0 flex flex-col bg-keep-surface'
          : 'px-3 pt-4 md:pt-6 pb-3 md:pb-4 flex justify-center'
      }
    >
      <div
        ref={wrapperRef}
        onFocus={startExpand}
        onPaste={handlePaste}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        tabIndex={-1}
        className={
          forceExpanded
            ? 'flex-1 min-h-0 flex flex-col bg-keep-surface'
            : `relative w-full max-w-[600px] bg-keep-surface border rounded-lg shadow-takenote ${
                dragActive ? 'border-keep-accent border-2' : 'border-keep-border'
              }`
        }
        style={
          forceExpanded
            ? undefined
            : {
                height: heightPx != null ? `${heightPx}px` : 'auto',
                overflow: heightPx != null ? 'hidden' : 'visible',
                transition: `height ${TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
              }
        }
        data-testid="take-note"
      >
        {!forceExpanded && (
          <div
            ref={collapsedMeasureRef}
            aria-hidden="true"
            className="px-4 py-3 flex items-center gap-2 absolute inset-x-0 top-0 invisible pointer-events-none"
          >
            <input type="text" readOnly tabIndex={-1} className="flex-1 outline-none text-[15px]" />
            <div className="p-2.5">
              <Paperclip className="w-5 h-5" />
            </div>
          </div>
        )}
        {!isExpanded ? (
          <div className="px-4 py-3 flex items-center gap-2">
            <input
              type="text"
              placeholder={t('takeNote.takeANote')}
              onFocus={startExpand}
              readOnly
              className="flex-1 outline-none text-[15px] bg-transparent text-keep-text placeholder:text-keep-textMuted cursor-text"
              data-testid="take-note-collapsed-input"
            />
            <button
              className="p-2.5 rounded-full hover:bg-keep-hover"
              aria-label={t('takeNote.attachPdf')}
              data-testid="take-note-pdf-button"
              onClick={openFilePicker}
              onFocus={(e) => e.stopPropagation()}
              type="button"
            >
              <Paperclip className="w-5 h-5 text-keep-textMuted" />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              multiple
              onChange={handleFile}
              className="hidden"
              data-testid="take-note-pdf-input"
            />
          </div>
        ) : (
          <div
            ref={formRef}
            className={
              forceExpanded
                ? 'flex-1 min-h-0 flex flex-col px-4 pt-6 pb-3'
                : `px-4 pt-3 pb-2 transition-opacity duration-150 ${
                    closing ? 'opacity-0' : 'opacity-100 animate-fade-in'
                  }`
            }
          >
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('takeNote.titlePlaceholder')}
              className={`w-full outline-none text-keep-text placeholder:text-keep-textMuted bg-transparent flex-shrink-0 ${
                forceExpanded ? 'text-[24px] font-semibold mb-3' : 'text-[16px] font-medium mb-2'
              }`}
              data-testid="take-note-title"
            />
            <div
              ref={bodyRef}
              contentEditable
              suppressContentEditableWarning
              onInput={handleBodyInput}
              onKeyDown={handleBodyKeyDown}
              data-placeholder={bodyPlaceholder}
              autoFocus
              className={`w-full outline-none text-keep-text bg-transparent whitespace-pre-wrap break-words empty:before:content-[attr(data-placeholder)] empty:before:text-keep-textMuted ${
                forceExpanded ? 'flex-1 min-h-0 overflow-y-auto text-[16px]' : 'text-[15px] min-h-[4.5em]'
              }`}
              data-testid="take-note-body"
            />
            {urlDetected && (
              <div
                className="text-[12px] text-keep-textMuted flex items-center gap-1 mt-1 flex-shrink-0"
                data-testid="take-note-url-hint"
              >
                {ytDetected ? (
                  <Youtube className="w-3.5 h-3.5 text-keep-ytRed" />
                ) : (
                  <Link2 className="w-3.5 h-3.5 text-keep-urlBlue" />
                )}
                <span>{ytDetected ? t('takeNote.detectedYoutube') : t('takeNote.detectedLink')}</span>
              </div>
            )}
            {multiUrlDetected && (
              <div
                className="text-[12px] text-keep-textMuted flex items-center gap-1 mt-1 flex-shrink-0"
                data-testid="take-note-multi-url-hint"
              >
                <Link2 className="w-3.5 h-3.5 text-keep-urlBlue" />
                <span>{t('takeNote.multiLinkDetected', { n: detectedUrls.length })}</span>
              </div>
            )}

            {pdfFiles.length > 0 && (
              <div
                className="flex items-center gap-2 mt-3 pb-1 overflow-x-auto flex-shrink-0"
                data-testid="take-note-pdf-carousel"
              >
                {pdfFiles.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="flex-shrink-0 flex items-center gap-2 w-48 rounded-lg border border-keep-border bg-keep-hover px-2.5 py-2"
                    data-testid="take-note-pdf-card"
                  >
                    <FileText className="w-5 h-5 text-keep-ytRed flex-shrink-0" />
                    <span className="flex-1 min-w-0 text-[12.5px] text-keep-text truncate">
                      {f.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removePdfFile(i)}
                      className="p-0.5 rounded-full hover:bg-keep-surface flex-shrink-0"
                      aria-label={t('takeNote.removeAttachment')}
                      data-testid="take-note-pdf-card-remove"
                    >
                      <X className="w-3.5 h-3.5 text-keep-textMuted" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {imageFiles.length > 0 && (
              <div
                className="flex items-center gap-2 mt-3 pb-1 overflow-x-auto flex-shrink-0"
                data-testid="take-note-image-carousel"
              >
                {imageFiles.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-keep-border bg-keep-hover"
                    data-testid="take-note-image-card"
                  >
                    <img
                      src={imagePreviewUrls[i]}
                      alt={f.name}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeImageFile(i)}
                      className="absolute top-1 right-1 p-0.5 rounded-full bg-black/60 hover:bg-black/80"
                      aria-label={t('takeNote.removeAttachment')}
                      data-testid="take-note-image-card-remove"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {audioFiles.length > 0 && (
              <div
                className="flex items-center gap-2 mt-3 pb-1 overflow-x-auto flex-shrink-0"
                data-testid="take-note-audio-carousel"
              >
                {audioFiles.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="flex-shrink-0 flex items-center gap-2 w-48 rounded-lg border border-keep-border bg-keep-hover px-2.5 py-2"
                    data-testid="take-note-audio-card"
                  >
                    <Music className="w-5 h-5 text-keep-accentDark flex-shrink-0" />
                    <span className="flex-1 min-w-0 text-[12.5px] text-keep-text truncate">
                      {f.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAudioFile(i)}
                      className="p-0.5 rounded-full hover:bg-keep-surface flex-shrink-0"
                      aria-label={t('takeNote.removeAttachment')}
                      data-testid="take-note-audio-card-remove"
                    >
                      <X className="w-3.5 h-3.5 text-keep-textMuted" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div
              className={
                forceExpanded
                  ? 'flex items-center gap-3 mt-3 pb-2 flex-shrink-0'
                  : 'flex items-center justify-between mt-3 pt-1'
              }
            >
              <div
                className={
                  forceExpanded
                    ? 'flex-1 flex items-center justify-center gap-2'
                    : 'flex items-center gap-2'
                }
              >
                <div className="relative">
                  {catPickerMounted ? (
                    // Invisible placeholder holding the button's exact footprint in
                    // the flex row — the real, visible button moves into the merged
                    // shape below so the Attach button next to it doesn't jump.
                    <div
                      aria-hidden="true"
                      className={
                        forceExpanded
                          ? 'invisible px-3 py-2 flex items-center gap-1.5 text-[13px]'
                          : 'invisible px-2 py-1 flex items-center gap-1 text-[12px]'
                      }
                    >
                      <Tag className="w-3.5 h-3.5" />
                      {selectedCat ? selectedCat.name : t('common.category')}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={toggleCatPicker}
                      className={
                        forceExpanded
                          ? `px-3 py-2 rounded-full flex items-center gap-1.5 text-[13px] bg-keep-surface shadow-keepHover border ${
                              selectedCat
                                ? 'text-keep-accentDark border-keep-accent'
                                : 'text-keep-textMuted border-keep-border'
                            }`
                          : `px-2 py-1 rounded-full flex items-center gap-1 text-[12px] ${
                              selectedCat
                                ? 'bg-keep-accentBgSoft text-keep-accentDark border border-keep-accent'
                                : 'text-keep-textMuted hover:bg-keep-hover'
                            }`
                      }
                      data-testid="take-note-category-button"
                    >
                      <Tag className="w-3.5 h-3.5" />
                      {selectedCat ? selectedCat.name : t('common.category')}
                    </button>
                  )}
                  {catPickerMounted && (
                    // One shape, one border, one shadow, cut to a "flag" silhouette
                    // with clip-path: full width where the list is, narrow to just
                    // the button row's own footprint at the row itself — so it
                    // never covers whatever sits next to the button (the attach
                    // button). A plain rectangle here would either need two
                    // separately-bordered/shadowed boxes (their box-shadows still
                    // glow at the seam no matter how the touching border/radius is
                    // trimmed — shadows aren't confined by border-width) or cover
                    // the full list width even at the row, hiding neighbors.
                    <div
                      className={`absolute left-0 z-40 flex flex-col bg-keep-surface border border-keep-border shadow-keepHover rounded-lg ${
                        forceExpanded ? 'bottom-0' : 'top-0'
                      }`}
                      style={{ width: CAT_LIST_WIDTH, clipPath: catClipPath }}
                    >
                      {!forceExpanded && (
                        <button
                          ref={catButtonRowRef}
                          type="button"
                          onClick={toggleCatPicker}
                          className="self-start px-2 py-1 flex items-center gap-1 text-[12px] text-keep-textMuted flex-shrink-0"
                          data-testid="take-note-category-button"
                        >
                          <Tag className="w-3.5 h-3.5" />
                          {selectedCat ? selectedCat.name : t('common.category')}
                        </button>
                      )}
                      <div
                        ref={catPickerBoxRef}
                        className="max-h-[240px] py-1"
                        style={{
                          height: catHeightPx != null ? `${catHeightPx}px` : 'auto',
                          overflowY: catHeightPx != null ? 'hidden' : 'auto',
                          transition: `height ${CAT_TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                        }}
                        data-testid="take-note-category-picker"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setCategoryId(null);
                            closeCatPicker();
                          }}
                          className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-keep-hover flex items-center gap-2"
                        >
                          <span className="w-3 h-3 rounded-full border border-keep-border" /> {t('common.none')}
                          {!categoryId && <Check className="w-3 h-3 ml-auto text-keep-accent" />}
                        </button>
                        {(categories || []).map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => {
                              setCategoryId(c.id);
                              closeCatPicker();
                            }}
                            className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-keep-hover flex items-center gap-2"
                          >
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{ background: c.color || '#34A853' }}
                            />
                            {c.name}
                            {categoryId === c.id && (
                              <Check className="w-3 h-3 ml-auto text-keep-accent" />
                            )}
                          </button>
                        ))}
                        <div className="border-t border-keep-border my-1" />
                        <button
                          type="button"
                          onClick={addNewCat}
                          className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-keep-hover flex items-center gap-2 text-keep-accentDark"
                          data-testid="take-note-category-add"
                        >
                          <Plus className="w-3 h-3" /> {t('takeNote.newCategory')}
                        </button>
                      </div>
                      {forceExpanded && (
                        <button
                          ref={catButtonRowRef}
                          type="button"
                          onClick={toggleCatPicker}
                          className="self-start px-3 py-2 flex items-center gap-1.5 text-[13px] text-keep-textMuted flex-shrink-0"
                          data-testid="take-note-category-button"
                        >
                          <Tag className="w-3.5 h-3.5" />
                          {selectedCat ? selectedCat.name : t('common.category')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="relative" ref={projPickerRef}>
                  <button
                    type="button"
                    onClick={() => setProjPickerOpen((v) => !v)}
                    className={
                      forceExpanded
                        ? `px-3 py-2 rounded-full flex items-center gap-1.5 text-[13px] bg-keep-surface shadow-keepHover border ${
                            selectedProj
                              ? 'text-keep-accentDark border-keep-accent'
                              : 'text-keep-textMuted border-keep-border'
                          }`
                        : `px-2 py-1 rounded-full flex items-center gap-1 text-[12px] ${
                            selectedProj
                              ? 'bg-keep-accentBgSoft text-keep-accentDark border border-keep-accent'
                              : 'text-keep-textMuted hover:bg-keep-hover'
                          }`
                    }
                    data-testid="take-note-project-button"
                  >
                    <Folder className="w-3.5 h-3.5" />
                    {selectedProj ? selectedProj.name : t('common.project')}
                  </button>
                  {projPickerOpen && (
                    <div
                      className={`absolute left-0 z-40 flex flex-col bg-keep-surface border border-keep-border shadow-keepHover rounded-lg max-h-[240px] overflow-y-auto py-1 ${
                        forceExpanded ? 'bottom-full mb-1' : 'top-full mt-1'
                      }`}
                      style={{ width: CAT_LIST_WIDTH }}
                      data-testid="take-note-project-picker"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setProjectId(null);
                          setProjPickerOpen(false);
                        }}
                        className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-keep-hover flex items-center gap-2"
                      >
                        <span className="w-3 h-3 rounded-full border border-keep-border" /> {t('common.none')}
                        {!projectId && <Check className="w-3 h-3 ml-auto text-keep-accent" />}
                      </button>
                      {(projects || []).map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setProjectId(p.id);
                            setProjPickerOpen(false);
                          }}
                          className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-keep-hover flex items-center gap-2"
                        >
                          <span
                            className="w-3 h-3 rounded-full"
                            style={{ background: p.color || '#34A853' }}
                          />
                          {p.name}
                          {projectId === p.id && (
                            <Check className="w-3 h-3 ml-auto text-keep-accent" />
                          )}
                        </button>
                      ))}
                      <div className="border-t border-keep-border my-1" />
                      <button
                        type="button"
                        onClick={addNewProj}
                        className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-keep-hover flex items-center gap-2 text-keep-accentDark"
                        data-testid="take-note-project-add"
                      >
                        <Plus className="w-3 h-3" /> {t('takeNote.newProject')}
                      </button>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={openFilePicker}
                  className={
                    forceExpanded
                      ? 'relative z-50 w-9 h-9 rounded-full bg-keep-surface border border-keep-border shadow-keepHover flex items-center justify-center flex-shrink-0'
                      : 'relative z-50 p-2 rounded-full hover:bg-keep-hover'
                  }
                  aria-label={t('takeNote.attachPdf')}
                  data-testid="take-note-expanded-pdf"
                >
                  <Paperclip className="w-4 h-4 text-keep-textMuted" />
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/pdf"
                  multiple
                  onChange={handleFile}
                  className="hidden"
                  data-testid="take-note-pdf-input"
                />
                <button
                  type="button"
                  onClick={openImagePicker}
                  className={
                    forceExpanded
                      ? 'relative z-50 w-9 h-9 rounded-full bg-keep-surface border border-keep-border shadow-keepHover flex items-center justify-center flex-shrink-0'
                      : 'relative z-50 p-2 rounded-full hover:bg-keep-hover'
                  }
                  aria-label={t('takeNote.attachImage')}
                  data-testid="take-note-expanded-image"
                >
                  <ImageIcon className="w-4 h-4 text-keep-textMuted" />
                </button>
                <input
                  ref={imageFileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageFile}
                  className="hidden"
                  data-testid="take-note-image-input"
                />
                <button
                  type="button"
                  onClick={openAudioPicker}
                  className={
                    forceExpanded
                      ? 'relative z-50 w-9 h-9 rounded-full bg-keep-surface border border-keep-border shadow-keepHover flex items-center justify-center flex-shrink-0'
                      : 'relative z-50 p-2 rounded-full hover:bg-keep-hover'
                  }
                  aria-label={t('takeNote.attachAudio')}
                  data-testid="take-note-expanded-audio"
                >
                  <Music className="w-4 h-4 text-keep-textMuted" />
                </button>
                <input
                  ref={audioFileRef}
                  type="file"
                  accept="audio/*"
                  multiple
                  onChange={handleAudioFile}
                  className="hidden"
                  data-testid="take-note-audio-input"
                />
              </div>
              {forceExpanded ? (
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitDisabled}
                  className="w-11 h-11 rounded-full bg-keep-accent text-keep-onAccent shadow-keepHover flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                  aria-label={t('common.done')}
                  data-testid="take-note-submit"
                >
                  {busy ? (
                    <Send className="w-4.5 h-4.5 animate-pulse" />
                  ) : (
                    <Check className="w-5 h-5" />
                  )}
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={closeCard}
                    className="px-3 py-1 text-[14px] text-keep-textMuted hover:bg-keep-hover rounded"
                    data-testid="take-note-cancel"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitDisabled}
                    className="px-4 py-1 text-[14px] font-medium text-keep-text hover:bg-keep-hover rounded disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                    data-testid="take-note-submit"
                  >
                    {busy ? <Send className="w-3.5 h-3.5 animate-pulse" /> : null}
                    {t('common.done')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default TakeNote;
